// packages
import classNames from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
// classes
import Jewelry from "../classes/Jewelry";

// components
import Accordion from "./Accordion";
import Compare from "./Compare";
import ItemGrid from "./ItemGrid";
import Pagination from "./Pagination";
import RangeNumeric from "./RangeNumeric";
import SelectImages from "./SelectImages";
import Tabs from "./Tabs";

// contexts
import { useComparison } from "../contexts/ComparisonContext";
import { useSearch } from "../contexts/SearchContext";

// utils
import { formattedCurrency } from "../utils/currency";
import Alert from "./Alert";

const DEFAULT_THEME = {
  accordion: {
    expandButton: {
      all: "",
      active: "bg-gray-600 text-white",
      inactive: "bg-t-red text-white",
    },
  },
  button: {
    primary: "bg-gray-600 text-white hover:bg-t-red hover:text-white",
  },
  compare: {
    button: {
      primary: "bg-gray-600 text-white hover:bg-t-red hover:text-white",
    },
  },
  input: "border-gray-600 focus:border-t-red",
  multiselect: {
    title: "text-black",
    select: {
      control: ({ isDisabled, isFocused }) =>
        classNames(
          !isDisabled && isFocused && "border-t-red",
          isFocused && "shadow-[0_0_0_1px] shadow-t-red",
          isFocused && "hover:border-t-red",
        ),
      option: ({ isDisabled, isFocused, isSelected }) =>
        classNames(
          isSelected && "bg-t-red",
          !isSelected && isFocused && "bg-t-red text-white",
          !isDisabled && isSelected && "active:bg-t-red",
          !isDisabled && !isSelected && "active:bg-t-red",
        ),
    },
  },
  radio:
    "border-gray-400 bg-gray-50 hover:bg-gray-200 checked:bg-t-red checked:hover:bg-t-red/50 checked:focus:bg-t-red/50",
  rangeNumeric: {
    input: "border-gray-600 focus:border-t-red",
    mark: "bg-t-red",
    thumb: "bg-t-red text-white focus:ring-t-red/40",
    title: "text-black",
    track: {
      active: "bg-t-red",
      inactive: "bg-gray-400",
    },
  },
  rangeValues: {
    label: {
      active: "text-black",
      inactive: "text-gray-400",
    },
    mark: "bg-t-red",
    thumb: "bg-t-red text-white focus:ring-t-red/40",
    title: "text-black",
    track: {
      active: "bg-t-red",
      inactive: "bg-gray-400",
    },
  },
  selectImages: {
    option: {
      all: "",
      active: "border-t-red bg-gray-50",
      hover: "hover:bg-gray-100",
      inactive: "border-white",
    },
    title: "text-black",
  },
};

export default function JewelryUI({ theme = {} }) {
  const activeTheme = _.merge({}, DEFAULT_THEME, theme);
  const location = useLocation();

  const [currentCategory, setCurrentCategory] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("category") || "";
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newCategory = params.get("category") || "";

    if (newCategory !== currentCategory) {
      setCurrentCategory(newCategory);

      // Reset filters
      setSearchParams((prevParams) => ({
        ...prevParams,
        sortBy: "",
        sortDir: "",
        description: "",
        metalGramsMax: "",
        metalGramsMin: "",
        priceMax: "",
        priceMin: "",
        on_hand: "",
        item_no: "",
        metal_karat: [],
        metalColor: [],
        metalType: [],
        stoneType: [],
        stoneShape: [],
        size: [],
        subCategory: [],
        design: [],
        category: newCategory,
      }));

      setCurrentPage(1);
    }
  }, [location.search]);

  //////////////////////////////////////////////////////////////////////////////
  // contexts
  //////////////////////////////////////////////////////////////////////////////
  const {
    // data
    results,

    // search
    searchParams,
    setSearchParams,
    setCurrentPage,
    searchProperties,
    // handleSearch,
    handleInputChange,
    fetchSearchProperties,
    expandAdvancedSearch,

    // reset
    handleReset,

    // sort
    // handleSort,

    // pagination
    currentPage,
    totalPages,
    handlePageChange,

    // routes
    routeShow,
  } = useSearch();

  const { comparisonData } = useComparison();

  //////////////////////////////////////////////////////////////////////////////
  // search
  //////////////////////////////////////////////////////////////////////////////

  const [paramDescription, setParamDescription] = useState(
    searchParams.description,
  );

  const handleParamDescription = (e) => {
    const previousValue = e.target.getAttribute("data-previous-value") || "";

    if (previousValue !== paramDescription && paramDescription.trim() !== "") {
      setSearchParams((prevParams) => ({
        ...prevParams,
        description: paramDescription,
      }));
      setCurrentPage(1);
      handleInputChange(e);
    } else if (paramDescription.trim() === "" && previousValue !== "") {
      setSearchParams((prevParams) => ({
        ...prevParams,
        description: "",
      }));
      setCurrentPage(1);
      handleInputChange(e);
    }
  };

  const [paramItemNo, setParamItemNo] = useState(searchParams.item_no);

  useEffect(() => {
    setParamItemNo(searchParams.item_no);
  }, [searchParams.item_no]);

  const handleParamItemNo = (e) => {
    const previousValue = e.target.getAttribute("data-previous-value") || "";

    if (previousValue !== paramItemNo && paramItemNo.trim() !== "") {
      setSearchParams((prevParams) => ({
        ...prevParams,
        item_no: paramItemNo,
      }));
      setCurrentPage(1);
    } else if (paramItemNo.trim() === "" && previousValue !== "") {
      setSearchParams((prevParams) => ({
        ...prevParams,
        item_no: "",
      }));
      setCurrentPage(1);
    }
  };

  //////////////////////////////////////////////////////////////////////////////
  // user
  //////////////////////////////////////////////////////////////////////////////

  const [userRole, setUserRole] = useState("");


  const paginationAndViewSwitcher = (
    <div className="flex justify-between">
      <Pagination
        currentPage={currentPage - 1}
        onPageChange={handlePageChange}
        pageCount={totalPages}
      />
    </div>
  );

  const sortOptions = [
    { label: "Stock Number, ascending", value: "item_no__asc" },
    { label: "Stock Number, descending", value: "item_no__desc" },
    { label: "Date, new to old", value: "entered_at__asc" },
    { label: "Date, old to new", value: "entered_at__desc" },
    { label: "Price, low to high", value: "price_total__asc" },
    { label: "Price, high to low", value: "price_total__desc" },
    { label: "Metal Color, A-Z", value: "metal_1_color__asc" },
    { label: "Metal Color, Z-A", value: "metal_1_color__desc" },
    { label: "Metal Karat, low to high", value: "metal_1_karat__asc" },
    { label: "Metal Karat, high to low", value: "metal_1_karat__desc" },
    { label: "Stone Shape, A-Z", value: "stone_1_shape_code__asc" },
    { label: "Stone Shape, Z-A", value: "stone_1_shape_code__desc" },
  ];

  //////////////////////////////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////////////////////////////

  const tabData = [
    {
      label: "Search",
      content: (
        <div className="mt-2 flex flex-col gap-4 md:mt-4 md:flex-row">
          <aside className="flex h-auto flex-col gap-6 md:w-1/4">
            <button
              className={`${activeTheme.button.primary} p-1.5 text-sm`}
              onClick={handleReset}
            >
              Reset Search
            </button>
            <SelectImages
              multiple={false}
              title="Category"
              name="category"
              selectedValues={searchParams.category}
              // theme={activeTheme.selectImages}
              onChange={(e) => {
                setSearchParams((prevParams) => ({
                  // Reset all filters except category
                  sortBy: "",
                  sortDir: "",
                  description: "",
                  metalGramsMax: "",
                  metalGramsMin: "",
                  priceMax: "",
                  priceMin: "",
                  on_hand: "",
                  item_no: "",
                  metal_karat: [],
                  metalColor: [],
                  metalType: [],
                  stoneType: [],
                  stoneShape: [],
                  size: [],
                  subCategory: [],
                  design: [],
                  // Set new category
                  category: e.target.value.map((value) => value.toUpperCase()),
                }));
                setCurrentPage(1);
              }}
              data={Jewelry.Categories()}
            />
            {/* TODO: fix valueCalculator and positionCalculator */}
            <RangeNumeric
              title="Price"
              limitMax={searchProperties.price_max}
              limitMin={searchProperties.price_min}
              // markMax={239}
              // markMin={0}
              // marks={[10, 109, 149]}
              // theme={activeTheme.rangeNumeric}
              valMax={searchParams.priceMax}
              valMin={searchParams.priceMin}
              callback={(min, max) => {
                setSearchParams((prevParams) => ({
                  ...prevParams,
                  priceMin: min,
                  priceMax: max,
                }));
                setCurrentPage(1);
              }}
              valueFormatter={(value) => {
                return formattedCurrency(value, 0);
              }}
            />
            <div>
              <p className="text-sm">Item Number</p>
              <input
                className={`${activeTheme.input} mt-2 w-full text-sm focus:ring-0`}
                data-previous-value=""
                name="item_no"
                onBlur={(e) => {
                  handleParamItemNo(e);
                }}
                onChange={(e) => {
                  setParamItemNo(e.target.value);
                }}
                onFocus={(e) => {
                  e.target.setAttribute("data-previous-value", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.code === "Enter" || e.code === "NumpadEnter") {
                    handleParamItemNo(e);
                  }
                }}
                placeholder="Search by Item Number"
                type="text"
                value={paramItemNo}
              />
            </div>
            <div>
              <p className="text-sm">Description</p>
              <input
                className={`${activeTheme.input} mt-2 w-full text-sm focus:ring-0`}
                data-previous-value=""
                name="description"
                onBlur={(e) => {
                  handleParamDescription(e);
                }}
                onChange={(e) => {
                  setParamDescription(e.target.value);
                }}
                onFocus={(e) => {
                  e.target.setAttribute("data-previous-value", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.code === "Enter" || e.code === "NumpadEnter") {
                    handleParamDescription(e);
                  }
                }}
                placeholder="Search by Description"
                type="text"
                value={paramDescription}
              />
            </div>
            {searchParams.category.length === 1 && (
              <div>
                <p className={`${activeTheme.multiselect.title} mb-2 text-sm`}>
                  Sub-Category
                </p>
                <Select
                  isClearable={true}
                  isMulti={true}
                  classNames={{
                    placeholder: () => "!text-xs !text-black",
                    option: () => "!lowercase first-letter:!uppercase !text-xs",
                    multiValueLabel: () =>
                      "!text-xs !lowercase first-letter:!uppercase !text-black",
                  }}
                  onChange={(values, _action) => {
                    setSearchParams((prevParams) => ({
                      ...prevParams,
                      subCategory: values.map((item) => item.value),
                    }));
                    setCurrentPage(1);
                  }}
                  options={(
                    _.get(searchProperties.category, searchParams.category) ||
                    []
                  )
                    .sort()
                    .map((item) => ({
                      label: item,
                      value: item,
                    }))}
                  placeholder="Sub-Category"
                  value={searchParams.subCategory.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                />
              </div>
            )}
            <div>
              <p className={`${activeTheme.multiselect.title} mb-2 text-sm`}>
                Stone Shape
              </p>
              <Select
                isClearable={true}
                isMulti={true}
                classNames={{
                  placeholder: () => "!text-xs !text-black",
                  option: () => "!lowercase first-letter:!uppercase !text-xs",
                  multiValueLabel: () =>
                    "!text-xs !lowercase first-letter:!uppercase !text-black",
                }}
                onChange={(values, _action) => {
                  setSearchParams((prevParams) => ({
                    ...prevParams,
                    stoneShape: values.map((item) => item.value),
                  }));
                  setCurrentPage(1);
                }}
                options={
                  searchProperties.stone_shape &&
                  searchProperties.stone_shape.map((item) => ({
                    label: item,
                    value: item,
                  }))
                }
                placeholder="Stone Shape"
                value={
                  searchParams.stoneShape &&
                  searchParams.stoneShape.map((item) => ({
                    label: item,
                    value: item,
                  }))
                }
              />
            </div>

            <Accordion
              isFirstTabActive={expandAdvancedSearch(searchParams)}
              // theme={activeTheme.accordion}
              data={[
                {
                  label: "Advanced Search",
                  content: (
                    <div className="mb-2 flex flex-col gap-6">
                      <div className="mt-6">
                        <p
                          className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                        >
                          Stone Type
                        </p>
                        <Select
                          isClearable={true}
                          isMulti={true}
                          classNames={{
                            placeholder: () => "!text-xs !text-black",
                            option: () =>
                              "!lowercase first-letter:!uppercase !text-xs",
                            multiValueLabel: () =>
                              "!text-xs !lowercase first-letter:!uppercase !text-black",
                          }}
                          onChange={(values, _action) => {
                            setSearchParams((prevParams) => ({
                              ...prevParams,
                              stoneType: values.map((item) => item.value),
                            }));
                            setCurrentPage(1);
                          }}
                          options={
                            searchProperties.stone_type &&
                            searchProperties.stone_type.map((item) => ({
                              label: item,
                              value: item,
                            }))
                          }
                          placeholder="Stone Type"
                          value={searchParams.stoneType.map((item) => ({
                            label: item,
                            value: item,
                          }))}
                        />
                      </div>
                      <div>
                        <p
                          className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                        >
                          Metal Type
                        </p>
                        <Select
                          isClearable={true}
                          isMulti={true}
                          classNames={{
                            placeholder: () => "!text-xs !text-black",
                            option: () =>
                              "!lowercase first-letter:!uppercase !text-xs",
                            multiValueLabel: () =>
                              "!text-xs !lowercase first-letter:!uppercase !text-black",
                          }}
                          onChange={(values, _action) => {
                            setSearchParams((prevParams) => ({
                              ...prevParams,
                              metalType: values.map((item) => item.value),
                            }));
                            setCurrentPage(1);
                          }}
                          options={
                            searchProperties.metal_type &&
                            searchProperties.metal_type.map((item) => ({
                              label: item,
                              value: item,
                            }))
                          }
                          placeholder="Metal Type"
                          value={searchParams.metalType.map((item) => ({
                            label: item,
                            value: item,
                          }))}
                        />
                      </div>
                      {searchParams.metalType != "PLATINUM" && (
                        <>
                          <div>
                            <p
                              className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                            >
                              Metal Color
                            </p>
                            <Select
                              isClearable={true}
                              isMulti={true}
                              classNames={{
                                placeholder: () => "!text-xs !text-black",
                                option: () =>
                                  "!lowercase first-letter:!uppercase !text-xs",
                                multiValueLabel: () =>
                                  "!text-xs !lowercase first-letter:!uppercase !text-black",
                              }}
                              onChange={(values, _action) => {
                                setSearchParams((prevParams) => ({
                                  ...prevParams,
                                  metalColor: values.map((item) => item.value),
                                }));
                                setCurrentPage(1);
                              }}
                              options={
                                searchProperties.metal_color &&
                                searchProperties.metal_color.map((item) => ({
                                  label: item,
                                  value: item,
                                }))
                              }
                              placeholder="Metal Color"
                              value={searchParams.metalColor.map((item) => ({
                                label: item,
                                value: item,
                              }))}
                            />
                          </div>
                          <div>
                            <p
                              className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                            >
                              Metal Karat
                            </p>
                            <Select
                              isClearable={true}
                              isMulti={true}
                              classNames={{
                                placeholder: () => "!text-xs !text-black",
                                option: () =>
                                  "!lowercase first-letter:!uppercase !text-xs",
                                multiValueLabel: () =>
                                  "!text-xs !lowercase first-letter:!uppercase !text-black",
                              }}
                              onChange={(values, _action) => {
                                setSearchParams((prevParams) => ({
                                  ...prevParams,
                                  metal_karat: values.map((item) => item.value),
                                }));
                                setCurrentPage(1);
                              }}
                              options={
                                searchProperties.metal_karat &&
                                searchProperties.metal_karat.map((item) => ({
                                  label: item,
                                  value: item,
                                }))
                              }
                              placeholder="Metal Karat"
                              value={searchParams.metal_karat.map((item) => ({
                                label: item,
                                value: item,
                              }))}
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <p
                          className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                        >
                          Setting Type
                        </p>
                        <Select
                          isClearable={true}
                          isMulti={true}
                          classNames={{
                            placeholder: () => "!text-xs !text-black",
                            option: () =>
                              "!lowercase first-letter:!uppercase !text-xs",
                            multiValueLabel: () =>
                              "!text-xs !lowercase first-letter:!uppercase !text-black",
                          }}
                          onChange={(values, _action) => {
                            setSearchParams((prevParams) => ({
                              ...prevParams,
                              design: values.map((item) => item.value),
                            }));
                            setCurrentPage(1);
                          }}
                          options={
                            searchProperties.design &&
                            searchProperties.design.map((item) => ({
                              label: item,
                              value: item,
                            }))
                          }
                          placeholder="Setting Type"
                          value={searchParams.design.map((item) => ({
                            label: item,
                            value: item,
                          }))}
                        />
                      </div>
                      <div>
                        <p
                          className={`${activeTheme.multiselect.title} mb-2 text-sm`}
                        >
                          Size
                        </p>
                        <Select
                          isClearable={true}
                          isMulti={true}
                          classNames={{
                            placeholder: () => "!text-xs !text-black",
                            option: () =>
                              "!lowercase first-letter:!uppercase !text-xs",
                            multiValueLabel: () =>
                              "!text-xs !lowercase first-letter:!uppercase !text-black",
                          }}
                          onChange={(values, _action) => {
                            setSearchParams((prevParams) => ({
                              ...prevParams,
                              size: values.map((item) => item.value),
                            }));
                            setCurrentPage(1);
                          }}
                          options={
                            searchProperties.size &&
                            searchProperties.size
                              .sort((a, b) => {
                                const aNum = parseFloat(a);
                                const bNum = parseFloat(b);

                                if (!isNaN(aNum) && !isNaN(bNum)) {
                                  return aNum - bNum;
                                }

                                if (!isNaN(aNum)) return -1;
                                if (!isNaN(bNum)) return 1;

                                return a.localeCompare(b);
                              })
                              .map((item) => ({
                                label: item,
                                value: item,
                              }))
                          }
                          placeholder="Size"
                          value={searchParams.size.map((item) => ({
                            label: item,
                            value: item,
                          }))}
                        />
                      </div>

                      <div className="flex items-start gap-x-2">
                        <input
                          type="checkbox"
                          id="onHand"
                          checked={searchParams.on_hand === "true"}
                          onChange={(e) => {
                            setSearchParams((prevParams) => ({
                              ...prevParams,
                              on_hand: e.target.checked ? "true" : "",
                            }));
                            setCurrentPage(1);
                          }}
                        />
                        <label
                          htmlFor="onHand"
                          className="cursor-pointer"
                        >
                          <p className="text-sm text-black">In House Only</p>
                        </label>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </aside>
          <main className="flex-1 overflow-x-auto">
            <div className="flex items-center justify-between max-sm:flex-col">
              {results.length > 0 && paginationAndViewSwitcher}
              <div className="w-full max-sm:mt-2 sm:w-1/2 md:w-1/4">
                <Select
                  isClearable={true}
                  isMulti={false}
                  classNames={{
                    placeholder: () => "!text-xs !text-black",
                    option: () => "!text-xs",
                    multiValueLabel: () => "!text-xs !text-black",
                  }}
                  // menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onChange={(option, _action) => {
                    setSearchParams((prevParams) => ({
                      ...prevParams,
                      sortBy: option ? option.value.split("__")[0] : "",
                      sortDir: option ? option.value.split("__")[1] : "",
                    }));
                    setCurrentPage(1);
                  }}
                  options={sortOptions}
                  placeholder="Sort by"
                  value={(function () {
                    const selectedValue =
                      searchParams.sortBy && searchParams.sortDir
                        ? `${searchParams.sortBy}__${searchParams.sortDir}`
                        : null;
                    return (
                      sortOptions.find((opt) => opt.value === selectedValue) ||
                      null
                    );
                  })()}
                />
              </div>
            </div>

            <div className="my-2 md:my-4">
              {results.length > 0 ? (
                <ItemGrid
                  data={results}
                  quoteType="jewelry"
                  type="jewelry"
                />
              ) : (
                <Alert
                  content="No matches found"
                  type="warning"
                  visible={true}
                />
              )}
            </div>
            {results.length > 0 && paginationAndViewSwitcher}
          </main>
        </div>
      ),
    },
    {
      label: `Compare (${comparisonData.length})`,
      content: <Compare />,
    },
  ];

  return <Tabs data={tabData} />;
}
